import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import api from 'api/api'
import { radioNowFeed } from 'state/radioNowFeed'
import { isLiveRadioStream } from 'components/panels/feedNewItemsEligibility'
import { prependRadioHead } from './radioStreamHead'
import { GetQuestsResponse } from 'api/api'
import { Quest } from 'types/quests'
import { getCachedQuest } from 'state/cache'
import { Filter } from 'components/filters/useStreamFilters'
import { cacheQuestPreservingPendingOptimisticAnswers } from 'state/pendingOptimisticAnswers'
import { QUEST_STREAM_QUERY_POLICY, refetchFailedStreamOnReconnect } from 'api/queryPolicies'
import {
    collectStreamQuestIdsNeedingHydration,
    doesStreamQuestNeedHydration,
    isHydratedStreamQuestEntity,
    selectHydratedStreamQuestEntities,
    selectQuestIdsForStreamQuestHydration,
} from './streamQuestHydration'
import {
    applyStreamQuestRowContext,
    buildStreamQuestRowRef,
    dedupeStreamQuestRows,
    preserveStreamQuestArrayIdentity,
    stripStreamQuestRowContext,
    StreamQuestRowRef,
} from './streamQuestRows'
import { getQuestsQueryKey, serializeFilterKey } from './useGetQuestsQueryKey'
import {
    getSpaceNotificationBadgeEventVersion,
    isNotificationCountStale,
    markSpaceNotificationsCleared,
} from './notificationBadgeRefetchPolicy'
import useStore from 'state/knovStore'
import { canonicalizeQuestFilter } from 'lib/questFilterCanonicalization'
import { linkedFeedQueryKey } from 'lib/feedPagination'
import {
    getNaturalFeedSessionSnapshot,
    naturalFeedSessionKey,
    recordNaturalFeedSessionPage,
    subscribeToNaturalFeedSession,
    type NaturalFeedQuestDescriptor,
} from 'lib/naturalFeedSession'

export { getQuestsQueryKey, serializeFilterKey } from './useGetQuestsQueryKey'

// Stable identity for the disabled-hook return so consumer memos keyed on `quests`
// don't churn while the hook is off.
const EMPTY_QUESTS: Quest[] = []

export default function useGetQuests(
    filter: Filter,
    options: {
        enabled?: boolean
        radioListening?: boolean
        params?: { clear?: boolean; [key: string]: unknown }
        retainFeedSession?: boolean
        unboundedPages?: boolean
        // Hard loads of a feed URL with ?page=N (crawlers, deep links) start
        // the feed at that batch, making linked pagination chains real.
        initialPage?: number
        refetchOnMount?:
            | boolean
            | 'always'
            | ((query: { state: { isInvalidated: boolean } }) => boolean | 'always')
    } = {
        enabled: false,
        params: {},
    },
): {
    quests: Quest[]
    pinnedQuest: Quest | null
    isFetching: boolean
    isPending?: boolean
    isError?: boolean
    isFetchingNextPage: boolean
    hasNextPage: boolean
    nextPage?: number
    fetchNextPage: () => void
    naturalFeedRows: NaturalFeedQuestDescriptor[]
    naturalFeedPinnedQuest: NaturalFeedQuestDescriptor | null
    naturalFeedSessionKey: string | null
    refetch?: () => unknown
    // How many users the viewer follows; only present on feed (following) responses.
    followingCount?: number | null
} {
    const emptyResult = {
        quests: EMPTY_QUESTS,
        pinnedQuest: null,
        isFetching: null,
        isFetchingNextPage: null,
        hasNextPage: null,
        nextPage: undefined,
        fetchNextPage: undefined,
        followingCount: null,
        naturalFeedRows: [],
        naturalFeedPinnedQuest: null,
        naturalFeedSessionKey: null,
    }

    const shouldFetch = options.enabled && filter && !filter.questId

    const baseQueryKey = getQuestsQueryKey(filter, options.params)
    const queryKey = linkedFeedQueryKey(baseQueryKey, options.initialPage || 1)
    const filterKey = baseQueryKey[1]
    const retainedSessionKey = options.retainFeedSession ? naturalFeedSessionKey(queryKey) : null
    const subscribeToSession = useCallback(
        (listener: () => void) => subscribeToNaturalFeedSession(retainedSessionKey, listener),
        [retainedSessionKey],
    )
    const readSessionSnapshot = useCallback(
        () => getNaturalFeedSessionSnapshot(retainedSessionKey),
        [retainedSessionKey],
    )
    const naturalFeedSession = useSyncExternalStore(
        subscribeToSession,
        readSessionSnapshot,
        readSessionSnapshot,
    )
    const isRadioStream = isLiveRadioStream(filter)
    const isNotificationsStream = !!filter?.notifications
    //console.log('useGetQuests FILTER KEY', filterKey)

    const result = useInfiniteQuery({
        // A hard load at ?page=N caches under an extended key so it never
        // collides with the normal page-1 feed for the same filter. Prefix
        // invalidation by ['quests', filterKey] still matches both.
        queryKey,
        queryFn: async ({ pageParam = 1, signal }) => {
            const page = Number(pageParam) || 1
            const request = canonicalizeQuestFilter({ ...filter, ...options.params, page })
            const loadLiveHead = page === 1 && isRadioStream && options.radioListening
            const clearsNotifications =
                isNotificationsStream && page === 1 && !!options.params?.clear
            const clearSpaceId = clearsNotifications
                ? request.space_id ?? useStore.getState().activeSpaceId
                : null
            const badgeVersionBeforeClear = clearsNotifications
                ? getSpaceNotificationBadgeEventVersion(clearSpaceId)
                : 0
            // The broadcast snapshot and the stream page are independent reads.
            const [live, data] = await Promise.all([
                loadLiveHead ? radioNowFeed.refresh({ listening: true }) : null,
                api.getQuests(request, signal),
            ])
            if (signal.aborted) {
                if (signal.reason instanceof Error) throw signal.reason
                const error = new Error('Quest stream request aborted')
                error.name = 'AbortError'
                throw error
            }
            if (!data) throw new Error('Quest stream request failed')
            // The server marked this space's notifications seen while serving
            // this page. Ignore counts requested before now, which may predate
            // the clear. A notification event during the
            // request may be newer than the clear, so its own count refresh wins.
            if (
                clearsNotifications &&
                getSpaceNotificationBadgeEventVersion(clearSpaceId) === badgeVersionBeforeClear
            ) {
                markSpaceNotificationsCleared(clearSpaceId)
                // The clear can leave rows unread (ones search hasn't indexed
                // yet), so read the count back rather than assume zero.
                const countRequestedAt = Date.now()
                void api
                    .getNotificationsCount(clearSpaceId)
                    .then(numNoti => {
                        if (numNoti == null) return
                        // A later clear supersedes this read-back.
                        if (isNotificationCountStale(clearSpaceId, countRequestedAt)) return
                        if (
                            getSpaceNotificationBadgeEventVersion(clearSpaceId) !==
                            badgeVersionBeforeClear
                        )
                            return
                        useStore.getState().set(state => {
                            if (String(state.activeSpaceId) === String(clearSpaceId))
                                state.numNoti = numNoti
                            state.spaceNotiCounts = {
                                ...state.spaceNotiCounts,
                                [clearSpaceId]: numNoti,
                            }
                        })
                    })
                    .catch(error => console.error('Failed to load notification count:', error))
            }
            const responseQuests = loadLiveHead
                ? await prependRadioHead(data.quests || [], live, id =>
                      api.getQuestQuery(
                          id,
                          {
                              streamPreview: true,
                              skipStreamQueryTouch: !!options.retainFeedSession,
                          },
                          signal,
                      ),
                  )
                : data.quests || []
            if (responseQuests.length > 0) {
                responseQuests.forEach(quest => {
                    cacheQuestPreservingPendingOptimisticAnswers(
                        stripStreamQuestRowContext(quest),
                        {
                            source: 'useGetQuests.query',
                            skipStreamQueryTouch: !!options.retainFeedSession,
                        },
                    )
                })
            }
            // Normalize pinned quest key and cache it if present (already hydrated in api.getQuests)
            const typedData = (data || { quests: [] }) as GetQuestsResponse
            const normalizedPinnedQuest = typedData.pinned_quest || typedData.pinnedQuest || null
            if (normalizedPinnedQuest)
                cacheQuestPreservingPendingOptimisticAnswers(
                    stripStreamQuestRowContext(normalizedPinnedQuest),
                    {
                        source: 'useGetQuests.pinned',
                        skipStreamQueryTouch: !!options.retainFeedSession,
                    },
                )
            if (retainedSessionKey) {
                recordNaturalFeedSessionPage({
                    page,
                    pinnedQuest: normalizedPinnedQuest,
                    quests: responseQuests,
                    sessionKey: retainedSessionKey,
                    // Normal feeds retain page membership/order to avoid scroll jumps during
                    // background refreshes. Notifications and Radio explicitly follow the
                    // latest head, so their fresh page replaces the retained page.
                    replacePageOnRefresh: isNotificationsStream || isRadioStream,
                })
            }
            return {
                ...typedData,
                page,
                has_more: typedData.has_more ?? (data.quests?.length ?? 0) > 0,
                pinnedQuest: normalizedPinnedQuest,
                quests: responseQuests.map(buildStreamQuestRowRef),
            }
        },

        enabled: shouldFetch,
        staleTime: isNotificationsStream ? 0 : QUEST_STREAM_QUERY_POLICY.staleTime,
        gcTime: QUEST_STREAM_QUERY_POLICY.gcTime,
        retry: 0,
        // The live Radio stream follows the broadcast: re-entering it (e.g. from
        // the Artists tab) refreshes to the latest instead of showing the cached head.
        refetchOnMount: isRadioStream
            ? 'always'
            : options.refetchOnMount ?? (isNotificationsStream ? 'always' : true),
        refetchOnWindowFocus: false,
        refetchOnReconnect: refetchFailedStreamOnReconnect,
        structuralSharing: false,
        networkMode: 'always',
        maxPages:
            options.unboundedPages && !options.retainFeedSession
                ? undefined
                : QUEST_STREAM_QUERY_POLICY.maxPages,
        meta: {
            protectsStreamEntities: !options.retainFeedSession,
        },

        initialPageParam: options.initialPage || 1,
        getNextPageParam: lastPage => {
            if (typeof lastPage.has_more === 'boolean') {
                return lastPage.has_more ? lastPage.page + 1 : undefined
            }

            // Continue fetching if we got any quests
            return lastPage.quests && lastPage.quests.length > 0 ? lastPage.page + 1 : undefined
        },
    })

    const rawPinnedQuest = result.data?.pages[0]?.pinnedQuest || null
    const retainedPinnedQuestId = naturalFeedSession.pinnedQuest?.row?.id
    const retainedPinnedQuest = retainedPinnedQuestId
        ? getCachedQuest(String(retainedPinnedQuestId))
        : null
    const rawPinnedQuestId = rawPinnedQuest?.id != null ? String(rawPinnedQuest.id) : null
    // Drop quests that pagination returned on more than one page (feed reordered between
    // fetches), keeping the first occurrence so scroll position is preserved. Notification
    // streams dedupe on row identity so intentional multi-surfacing survives.
    const questRows = dedupeStreamQuestRows(result.data?.pages.flatMap(page => page.quests) || [], {
        keyByRow: isNotificationsStream,
        pinnedId: rawPinnedQuestId,
    })
    const questIds = questRows.map(quest => (quest?.id != null ? String(quest.id) : null))
    const cachedQuests = questIds.map(questId => (questId ? getCachedQuest(questId) : null))
    const cachedPinnedQuest =
        retainedPinnedQuest ||
        (rawPinnedQuest?.id
            ? getCachedQuest(String(rawPinnedQuest.id)) || rawPinnedQuest
            : rawPinnedQuest)
    const pinnedQuestHydrationIds =
        rawPinnedQuest?.id && doesStreamQuestNeedHydration(cachedPinnedQuest)
            ? [String(rawPinnedQuest.id)]
            : []
    const streamQuestIdsNeedingHydration = Array.from(
        new Set([
            ...collectStreamQuestIdsNeedingHydration(questIds, cachedQuests),
            ...pinnedQuestHydrationIds,
        ]),
    )
    const streamQuestIdsNeedingHydrationSignature = streamQuestIdsNeedingHydration.join(',')
    // The per-quest reads above return identity-stable objects (the normalized read cache in
    // state/cache is dependency-invalidated on entity writes, and applyStreamQuestRowContext
    // memoizes merges per object pair), so renders that changed nothing rebuild an element-wise
    // identical list. Preserve the previous array identity in that case: downstream row-model
    // memos (e.g. QuestStream's virtualStreamRowGeometry) key on this array and must only
    // recompute when a quest's content, row context, or membership actually changed.
    const questsRef = useRef<Quest[]>([])
    const quests = preserveStreamQuestArrayIdentity(
        questsRef.current,
        selectHydratedStreamQuestEntities(
            cachedQuests.map((quest, index) => applyStreamQuestRowContext(quest, questRows[index])),
        ),
    )
    questsRef.current = quests
    const pinnedQuest =
        cachedPinnedQuest && isHydratedStreamQuestEntity(cachedPinnedQuest)
            ? cachedPinnedQuest
            : null

    useEffect(() => {
        if (
            !shouldFetch ||
            options.retainFeedSession ||
            streamQuestIdsNeedingHydration.length === 0
        )
            return

        const hydrationIds = selectQuestIdsForStreamQuestHydration({
            questIds: streamQuestIdsNeedingHydration,
        })
        if (hydrationIds.length === 0) return

        hydrationIds.forEach(questId => {
            void api.getQuestQuery(questId).catch(error => {
                console.warn('>>> useGetQuests: stream quest hydration failed', {
                    questId,
                    filterKey,
                    error,
                })
            })
        })
    }, [filterKey, streamQuestIdsNeedingHydrationSignature, shouldFetch])

    // This lets us call the hook no matter what from Panel to ensure hooks are called in the same order every time without illegal conditional hooks or skipping useInfiniteQuery.
    // The right way to do this is to split the panels out and have them call the relevant hook, eg QuestPanel and QuestsPanel.
    if (!shouldFetch) {
        return emptyResult
    }

    // The next linked-pagination page for URL-addressable feeds (?page=N).
    const lastLoadedPage = result.data?.pages?.[result.data.pages.length - 1]
    const nextPage = result.hasNextPage && lastLoadedPage ? lastLoadedPage.page + 1 : undefined

    return {
        ...result,
        hasNextPage:
            options.retainFeedSession && naturalFeedSession.atCapacity ? false : result.hasNextPage,
        nextPage,
        quests,
        pinnedQuest,
        followingCount: result.data?.pages?.[0]?.following_count ?? null,
        naturalFeedRows: naturalFeedSession.rows,
        naturalFeedPinnedQuest: naturalFeedSession.pinnedQuest,
        naturalFeedSessionKey: retainedSessionKey,
    }
}
